import React, { useState, useEffect } from "react";
import { TelegramNotifyCard, NotificationChannelsCard } from "../../components/analyst/NotificationCards";

const getToken = () => localStorage.getItem("access_token") || localStorage.getItem("token");
const authH = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` });

function Toggle({ checked, onChange, label, hint }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "14px 0", borderBottom: "1px solid var(--border-subtle)" }}>
      <div style={{ flex: 1, paddingRight: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 3 }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5 }}>{hint}</div>}
      </div>
      <div onClick={() => onChange(!checked)}
        style={{ width: 40, height: 22, borderRadius: 11, cursor: "pointer", flexShrink: 0, marginTop: 2,
          background: checked ? "var(--accent)" : "var(--border)",
          position: "relative", transition: "background 0.2s" }}>
        <div style={{ position: "absolute", top: 3, left: checked ? 20 : 3, width: 16, height: 16,
          borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
      </div>
    </div>
  );
}

function ChangePasswordForm() {
  const [form, setForm] = React.useState({ current: "", newPass: "", confirm: "" });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPass !== form.confirm) { setError("New passwords do not match."); return; }
    setBusy(true); setError(""); setSuccess("");
    try {
      const res = await fetch("/api/profile/change-password", {
        method: "POST", headers: authH(),
        body: JSON.stringify({ current_password: form.current, new_password: form.newPass }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to change password");
      setSuccess("Password changed successfully!");
      setForm({ current: "", newPass: "", confirm: "" });
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const inp = { width: "100%", padding: "9px 12px", background: "var(--bg-surface)",
    border: "1px solid var(--border)", borderRadius: "var(--radius-md)", color: "var(--text-main)",
    fontSize: 13, outline: "none", boxSizing: "border-box" };
  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {error && <div style={{ fontSize: 12, color: "var(--danger)", padding: "8px 12px",
        background: "var(--danger-soft)", borderRadius: "var(--radius-md)" }}>{error}</div>}
      {success && <div style={{ fontSize: 12, color: "var(--success)", padding: "8px 12px",
        background: "var(--success-soft)", borderRadius: "var(--radius-md)" }}>{success}</div>}
      <input type="password" required style={inp} placeholder="Current password"
        value={form.current} onChange={e => setForm(f => ({ ...f, current: e.target.value }))} />
      <input type="password" required style={inp} placeholder="New password (min 8 chars, uppercase, number)"
        value={form.newPass} onChange={e => setForm(f => ({ ...f, newPass: e.target.value }))} />
      <input type="password" required style={inp} placeholder="Confirm new password"
        value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} />
      <button type="submit" disabled={busy} style={{ padding: "9px 20px", background: "var(--accent)",
        color: "#fff", border: "none", borderRadius: "var(--radius-md)", fontSize: 13,
        fontWeight: 600, cursor: "pointer", alignSelf: "flex-start", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Saving..." : "Change Password"}
      </button>
    </form>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)",
      borderRadius: "var(--radius-xl)", padding: "24px 28px", marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-main)" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function ChipSelect({ options, selected, onChange }) {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map(o => {
        const active = selected.includes(o.value);
        return (
          <div key={o.value} onClick={() => toggle(o.value)}
            style={{ padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s", userSelect: "none",
              background: active ? "var(--accent)" : "var(--bg-elevated)",
              color: active ? "#fff" : "var(--text-muted)",
              border: `1px solid ${active ? "var(--accent)" : "var(--border)"}` }}>
            {o.label}
          </div>
        );
      })}
    </div>
  );
}

const INSTRUMENTS = [
  { value: "gold", label: "Gold (XAUUSD)" },
  { value: "forex_major", label: "Forex Majors" },
  { value: "forex_minor", label: "Forex Minors" },
  { value: "indices", label: "Indices" },
  { value: "oil", label: "Oil / Commodities" },
  { value: "crypto", label: "Crypto" },
  { value: "stocks", label: "Stocks" },
];

const HOLDING_PERIODS = [
  { value: "scalping", label: "Scalping (< 1hr)" },
  { value: "intraday", label: "Intraday" },
  { value: "swing", label: "Swing (days)" },
  { value: "position", label: "Position (weeks+)" },
];

const TIMEZONES = [
  "UTC-8","UTC-5","UTC","UTC+1","UTC+2","UTC+3","UTC+5:30","UTC+7","UTC+8","UTC+9","UTC+10","UTC+12"
];

const ProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [prefsMsg, setPrefsMsg] = useState("");
  const [prefsMsgType, setPrefsMsgType] = useState("success");

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("");
  const [bio, setBio] = useState("");

  const [instruments, setInstruments] = useState([]);
  const [holdingPeriod, setHoldingPeriod] = useState("intraday");
  const [timezone, setTimezone] = useState("UTC+7");
  const [hideAccountNumber, setHideAccountNumber] = useState(false);
  const [hideTradeDetails, setHideTradeDetails] = useState(false);
  const [notifNewFollower, setNotifNewFollower] = useState(true);
  const [notifFeeReceived, setNotifFeeReceived] = useState(true);
  const [notifMarginCall, setNotifMarginCall] = useState(true);
  const [notifTradeCopied, setNotifTradeCopied] = useState(true);

  const roleLabelMap = { trader: "Trader", investor: "Investor", analyst: "Analyst", admin: "Admin" };
  const isTrader = role === "trader";
  const isAnalyst = role === "analyst";
  const showPrefs = isTrader || isAnalyst;

  useEffect(() => {
    const p1 = fetch("/api/profile", { headers: authH() }).then(r => r.json()).then(d => {
      setEmail(d.email || "");
      setRole(d.primary_role || "");
      setName(d.name || "");
      setPhone(d.phone_number || "");
      setCountry(d.country || "");
      setBio(d.bio || "");
    });
    const p2 = fetch("/api/profile/preferences", { headers: authH() }).then(r => r.json()).then(d => {
      if (d.ok && d.preferences) {
        const p = d.preferences;
        setInstruments(p.preferred_instruments || []);
        setHoldingPeriod(p.holding_period || "intraday");
        setTimezone(p.timezone || "UTC+7");
        setHideAccountNumber(p.hide_account_number || false);
        setHideTradeDetails(p.hide_trade_details || false);
        setNotifNewFollower(p.notif_new_follower !== false);
        setNotifFeeReceived(p.notif_fee_received !== false);
        setNotifMarginCall(p.notif_margin_call !== false);
        setNotifTradeCopied(p.notif_trade_copied !== false);
      }
    }).catch(() => {});
    Promise.all([p1, p2]).finally(() => setLoading(false));
  }, []);

  const showMsg = (text, type = "success") => {
    setMessage(text); setMsgType(type);
    setTimeout(() => setMessage(""), 3500);
  };
  const showPrefsMsg = (text, type = "success") => {
    setPrefsMsg(text); setPrefsMsgType(type);
    setTimeout(() => setPrefsMsg(""), 3500);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (phone && !/^\+[0-9]{7,15}$/.test(phone)) {
      showMsg("Phone number must be in international format, e.g. +628123456789", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch("/api/profile", {
        method: "PUT", headers: authH(),
        body: JSON.stringify({ name, phone_number: phone || undefined, country, bio }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      showMsg("Profile saved successfully.", "success");
    } catch (err) { showMsg(err.message, "error"); }
    finally { setSaving(false); }
  };

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      const res = await fetch("/api/profile/preferences", {
        method: "PUT", headers: authH(),
        body: JSON.stringify({
          preferred_instruments: instruments,
          holding_period: holdingPeriod,
          timezone,
          hide_account_number: hideAccountNumber,
          hide_trade_details: hideTradeDetails,
          notif_new_follower: notifNewFollower,
          notif_fee_received: notifFeeReceived,
          notif_margin_call: notifMarginCall,
          notif_trade_copied: notifTradeCopied,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      showPrefsMsg("Settings saved.", "success");
    } catch (err) { showPrefsMsg(err.message, "error"); }
    finally { setSavingPrefs(false); }
  };

  const inp = { width: "100%", padding: "10px 12px", background: "var(--bg)",
    border: "1px solid var(--border)", borderRadius: "var(--radius-md)", color: "var(--text-main)",
    fontSize: 14, boxSizing: "border-box", transition: "border-color 0.15s" };
  const readonlyInp = { ...inp, background: "var(--bg-subtle)", color: "var(--text-faint)", cursor: "not-allowed" };
  const labelSt = { display: "block", fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginBottom: 6 };
  const hintSt = { fontSize: 12, color: "var(--text-faint)", marginTop: 4 };

  const MsgBox = ({ msg, type }) => !msg ? null : (
    <div style={{ padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: 16,
      fontSize: 13, fontWeight: 500,
      background: type === "success" ? "var(--success-soft)" : "var(--danger-soft)",
      border: `1px solid ${type === "success" ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)"}`,
      color: type === "success" ? "var(--success)" : "var(--danger)" }}>{msg}</div>
  );

  const SaveBtn = ({ busy, label, onClick, isSubmit }) => (
    <button type={isSubmit ? "submit" : "button"} disabled={busy} onClick={onClick}
      style={{ padding: "10px 24px", background: "var(--accent)", border: "none",
        borderRadius: "var(--radius-md)", color: "#fff", fontSize: 14, fontWeight: 600,
        cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
      {busy ? "Saving..." : label}
    </button>
  );

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)" }}>Loading...</div>
  );

  return (
    <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text-main)" }}>Profile & Settings</h1>
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0" }}>Manage your public identity and account information.</p>
      </div>

      <MsgBox msg={message} type={msgType} />

      {/* Public Profile */}
      <Section title="Public Profile" subtitle="Shown on leaderboard, marketplace & public pages">
        <form onSubmit={handleSaveProfile}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={labelSt}>Display name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="Your display name" style={inp} />
              <div style={hintSt}>Avoid using your full name if you prefer privacy.</div>
            </div>
            <div>
              <label style={labelSt}>Primary role</label>
              <input type="text" value={roleLabelMap[role] || role} readOnly style={readonlyInp} />
              <div style={hintSt}>Role is set during onboarding. Contact support to change.</div>
            </div>
            <div>
              <label style={labelSt}>Login email</label>
              <input type="text" value={email} readOnly style={readonlyInp} />
              <div style={hintSt}>Email changes require a separate verification process.</div>
            </div>
            <div>
              <label style={labelSt}>Phone number</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+62..." style={inp} />
            </div>
            <div>
              <label style={labelSt}>Country / region</label>
              <input type="text" value={country} onChange={e => setCountry(e.target.value)}
                placeholder="e.g. ID, US, SG" style={inp} />
            </div>
            <div>
              <label style={labelSt}>Bio & trading style</label>
              <textarea value={bio} onChange={e => setBio(e.target.value)}
                placeholder="Describe your trading strategy, style, and experience..."
                rows={4} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
              <div style={hintSt}>Explain your strategy to potential investors and followers.</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <SaveBtn busy={saving} label="Save profile" isSubmit />
            </div>
          </div>
        </form>
      </Section>

      {/* Trading Preferences — trader & analyst */}
      {showPrefs && (
        <Section title="Trading Preferences" subtitle="Shown on your public trader profile">
          <MsgBox msg={prefsMsg} type={prefsMsgType} />
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div>
              <label style={labelSt}>Preferred instruments</label>
              <ChipSelect options={INSTRUMENTS} selected={instruments} onChange={setInstruments} />
              <div style={hintSt}>Select all that apply. Helps investors find you by trading style.</div>
            </div>
            <div>
              <label style={labelSt}>Typical holding period</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {HOLDING_PERIODS.map(h => (
                  <div key={h.value} onClick={() => setHoldingPeriod(h.value)}
                    style={{ padding: "7px 16px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: "pointer", userSelect: "none",
                      background: holdingPeriod === h.value ? "var(--accent)" : "var(--bg-elevated)",
                      color: holdingPeriod === h.value ? "#fff" : "var(--text-muted)",
                      border: `1px solid ${holdingPeriod === h.value ? "var(--accent)" : "var(--border)"}` }}>
                    {h.label}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label style={labelSt}>Timezone</label>
              <select value={timezone} onChange={e => setTimezone(e.target.value)}
                style={{ ...inp, cursor: "pointer" }}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
              <div style={hintSt}>Helps investors understand your active trading hours.</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <SaveBtn busy={savingPrefs} label="Save preferences" onClick={handleSavePrefs} />
            </div>
          </div>
        </Section>
      )}

      {/* Privacy & Visibility — trader & analyst */}
      {showPrefs && (
        <Section title="Privacy & Visibility" subtitle="Control what investors see on your public profile">
          <MsgBox msg={prefsMsg} type={prefsMsgType} />
          <div>
            {isTrader && (
              <Toggle checked={hideAccountNumber} onChange={setHideAccountNumber}
                label="Hide account number"
                hint="Your MT4/MT5 account number will be masked (e.g. MT-****862) on your public profile and investor dashboard. Copy trading is not affected." />
            )}
            <Toggle checked={hideTradeDetails} onChange={setHideTradeDetails}
              label="Hide trade details"
              hint="SL, TP, and lot size will be hidden from your public trade history. Trade results (profit/loss) remain visible." />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <SaveBtn busy={savingPrefs} label="Save privacy settings" onClick={handleSavePrefs} />
          </div>
        </Section>
      )}

      {/* Notification Settings — trader & analyst */}
      {showPrefs && (
        <Section title="Notification Settings" subtitle="Email alerts for account activity">
          <div>
            <Toggle checked={notifNewFollower} onChange={setNotifNewFollower}
              label="New follower / investor"
              hint="Alert when an investor starts copying your account." />
            <Toggle checked={notifFeeReceived} onChange={setNotifFeeReceived}
              label="Performance fee received"
              hint="Monthly fee settlement notification." />
            <Toggle checked={notifMarginCall} onChange={setNotifMarginCall}
              label="Margin call alert"
              hint="Critical alert when your account hits margin call." />
            <Toggle checked={notifTradeCopied} onChange={setNotifTradeCopied}
              label="Trade copied"
              hint="Alert each time your trade is successfully copied to an investor." />
          </div>
          <div style={{ padding: "12px 14px", borderRadius: "var(--radius-md)", marginTop: 16,
            background: "rgba(78,154,241,0.07)", border: "1px solid rgba(78,154,241,0.2)",
            fontSize: 12, color: "var(--text-faint)" }}>
            Notifications are sent to your registered email address. In-app push notifications coming soon.
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <SaveBtn busy={savingPrefs} label="Save notifications" onClick={handleSavePrefs} />
          </div>
        </Section>
      )}

      {/* Security */}
      <Section title="Security" subtitle="Account security settings">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ padding: "14px 16px", background: "var(--bg-elevated)",
            borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 12 }}>Change Password</div>
            <ChangePasswordForm />
          </div>
          <div style={{ padding: "14px 16px", background: "var(--bg-elevated)",
            borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 4 }}>
              Two-factor authentication (2FA)
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Coming soon — 2FA via authenticator app will be available in a future update.
            </div>
          </div>
        </div>
      </Section>
      {isAnalyst && (
        <Section title="Notifications" subtitle="Telegram groups and Discord channels for signal alerts">
          <TelegramNotifyCard/>
          <div style={{ marginTop: 16 }}>
            <NotificationChannelsCard/>
          </div>
        </Section>
      )}
    </div>
  );
};

export default ProfilePage;
